import { useMemo, useState } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { OrganizationPicker } from '@insights_module/components/OrganizationPicker';
import { useOrganizations } from '@account_module/hooks/useOrganizations';
import { IOrganization } from '@account_module/types/Organizations';
import { Home } from '@icons/Home';
import { LibraryIcon } from '@icons/LibraryIcon';
import { GiftIcon } from '@icons/GiftIcon';
import { CaretIcon } from '@icons/CaretIcon';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { useAccountContext } from '@account_module/context/AccountContext';
import { SidebarToggleButton } from '@insights_module/components/SidebarToggleButton';
import { SCREENS, useMediaQueries } from '@app/hooks/useMediaQueries';
import { GearIcon } from '@icons/GearIcon';

const SidebarContainer = tw.aside`z-10 w-[320px] sm:w-[254px] h-full text-text-and-icon transition-all duration-200 max-w-[320px] sm:max-w-[254px] flex flex-col`;
const SidebarLink = tw(
  Link
)`flex flex-row items-center justify-start gap-[12px] my-[16px] overflow-hidden whitespace-nowrap text-text-and-icon no-underline`;
const Spacer = tw.div`w-full h-[1px] bg-gray-1`;
const SidebarLinkButton = tw.button`flex flex-row items-center justify-start gap-[12px] my-[16px] overflow-hidden whitespace-nowrap text-text-and-icon no-underline`;

const IntegrationsMenuWrapper = tw.div`absolute top-[25px] left-0 flex flex-col bg-transparent z-20 transition-all duration-200`;
const IntegrationsMenu = tw.div`flex flex-col w-auto bg-neutral-background border border-gray-1 rounded z-20 p-1 shadow-dropdown`;
const IntegrationMenuLink = tw(
  Link
)`flex flex-row items-center justify-start w-full p-[6px] rounded-[3px] h-[35px] text-[12px] cursor-pointer hover:bg-gray-2 text-text-and-icon-muted hover:no-underline whitespace-nowrap`;

const SidebarHeader = () => {
  const { data, isLoading } = useOrganizations();
  const [isMobile] = useMediaQueries(SCREENS.SM);

  const organizations = [...(data || [])] as IOrganization[];

  if (isLoading && !data) return null;

  return (
    <div tw='w-full flex items-center justify-between border-b border-solid border-gray-1 p-[8px] pl-[16px] h-[41px]'>
      <OrganizationPicker organizations={organizations} />
      {!isMobile && <SidebarToggleButton mode='open' />}
    </div>
  );
};

export const InsightsSidebarNav = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentPage } = useAccountContext();

  const { isEnabled: isMegaphoneIntegrationEnabled } = useFeatureFlag(
    FEATURE_FLAG.MEGAPHONE_INTEGRATION
  );
  const { isEnabled: isIHMStarshipIntegrationEnabled } = useFeatureFlag(
    FEATURE_FLAG.IHM_STARSHIP_INTEGRATION
  );
  const { isEnabled: isInsightsOverviewPageEnabled } = useFeatureFlag(
    FEATURE_FLAG.INSIGHTS_OVERVIEW_PAGE
  );

  const [isIntegrationsMenuVisible, setIsIntegrationsMenuVisible] = useState<boolean>(false);

  // if new integration is added, we need to add a new condition here
  const hasMoreThenOneIntegration = useMemo(() => {
    return isMegaphoneIntegrationEnabled && isIHMStarshipIntegrationEnabled;
  }, [isMegaphoneIntegrationEnabled, isIHMStarshipIntegrationEnabled]);

  const handleIntegrationClick = () => {
    if (hasMoreThenOneIntegration) {
      setIsIntegrationsMenuVisible(!isIntegrationsMenuVisible);
      return;
    }

    if (isMegaphoneIntegrationEnabled) {
      navigate('/insights/integrations/megaphone');
      return;
    }

    if (isIHMStarshipIntegrationEnabled) {
      navigate('/insights/integrations/triton');
    }
  };

  return (
    <SidebarContainer>
      <SidebarHeader />
      <div tw='overflow-hidden px-[16px] h-full'>
        <SidebarLink
          to={isInsightsOverviewPageEnabled ? '/insights' : '#'}
          css={[
            currentPage === 'overview' && tw`font-semibold underline`,
            !isInsightsOverviewPageEnabled && tw`pointer-events-none opacity-50`,
          ]}>
          <Home tw='stroke-text-and-icon w-[15px] h-[15px]' />
          {t('insights.sidebar_nav.overview')}
        </SidebarLink>
        <Spacer />
        <SidebarLink
          to='/insights/shows'
          css={[currentPage === 'shows' && tw`font-semibold underline`]}>
          <LibraryIcon tw='stroke-text-and-icon w-[15px] h-[15px]' />
          {t('insights.sidebar_nav.my_shows')}
        </SidebarLink>
        <SidebarLink
          to='/insights/episodes'
          css={[currentPage === 'episodes' && tw`font-semibold underline`]}>
          <LibraryIcon tw='stroke-text-and-icon w-[15px] h-[15px]' />
          {t('insights.sidebar_nav.my_episodes')}
        </SidebarLink>
        <Spacer />
        <SidebarLinkButton
          tw='relative overflow-visible'
          onClick={handleIntegrationClick}
          css={[currentPage === 'integrations' && tw`font-semibold underline`]}>
          <GiftIcon tw='stroke-text-and-icon w-[15px] h-[15px]' />
          {t('insights.sidebar_nav.my_collections')}
          {hasMoreThenOneIntegration && (
            <CaretIcon
              tw='fill-text-and-icon-muted'
              css={[currentPage === 'integrations' && tw`fill-text-and-icon`]}
            />
          )}

          {isIntegrationsMenuVisible && (
            <IntegrationsMenuWrapper>
              <IntegrationsMenu>
                {isMegaphoneIntegrationEnabled && (
                  <IntegrationMenuLink to='/insights/integrations/megaphone'>
                    Megaphone Integration
                  </IntegrationMenuLink>
                )}
                {isIHMStarshipIntegrationEnabled && (
                  <IntegrationMenuLink to='/insights/integrations/triton'>
                    Triton Integration
                  </IntegrationMenuLink>
                )}
              </IntegrationsMenu>
            </IntegrationsMenuWrapper>
          )}
        </SidebarLinkButton>
        <Spacer />
        <SidebarLink
          to='/account/settings'
          css={[currentPage === 'settings' && tw`font-semibold underline`]}>
          <GearIcon tw='stroke-text-and-icon-muted' />
          {t('insights.sidebar_nav.settings')}
        </SidebarLink>
        {/* currently we don't have saved filters, my groups and favorites support */}
        {/* <SidebarLink
          to='saved-filters'
          css={[currentPage === 'saved-filters' && tw`font-semibold underline`]}>
          <MarkerIcon tw='stroke-text-and-icon w-[15px] h-[15px]' />
          {t('insights.sidebar_nav.saved_filters')}
        </SidebarLink>
        <Spacer />
        <SidebarLink
          to='my-groups'
          css={[currentPage === 'my-groups' && tw`font-semibold underline`]}>
          <GroupsIcon tw='stroke-text-and-icon w-[15px] h-[15px]' />
          {t('insights.sidebar_nav.my_groups')}
        </SidebarLink>

        <SidebarLink
          to='favorites'
          css={[currentPage === 'favorites' && tw`font-semibold underline`]}>
          <HeartIcon tw='stroke-text-and-icon w-[15px] h-[15px]' />
          {t('insights.sidebar_nav.favorites')}
        </SidebarLink> */}
      </div>
    </SidebarContainer>
  );
};
